import {
  Component,
  Input,
  Output,
  EventEmitter,
  OnChanges,
  SimpleChanges,
  ChangeDetectionStrategy,
} from '@angular/core';
import { CommonModule } from '@angular/common';

export interface FontOption {
  name: string;
  value: string;
}

/**
 * FontSelectionModalComponent - A reusable modal dialog that allows users to preview and choose a font for branding or content, then apply or cancel that selection.
 * Purpose:
 *   - Presents a list of FontOption items, shows a live previewText using a temporarily selected font, and lets the user apply or cancel the selection.
 *   - Keeps a temporary selection (tempSelectedFont) while the modal is open so changes are not committed until the user clicks Apply.
 * Key methods:
 *   - ngOnChanges(changes: SimpleChanges): Lifecycle hook that initializes/updates tempSelectedFont when the modal opens or when the selectedFont input changes.
 *   - onClose(): Resets the temporary selection to the current selectedFont and emits the close event to request closing the modal without applying changes.
 *   - onFontSelect(font: FontOption): Updates tempSelectedFont when a font option is clicked/selected in the UI (does not emit selection yet).
 *   - onApply(): If the temporary selection differs from the current selectedFont, emits fontSelected with the new font value, then emits close to close the modal.
 *   - onBackdropClick(event: MouseEvent): Detects clicks on the modal overlay (class 'font-modal-overlay') and triggers onClose to dismiss the modal when the backdrop is clicked.
 * Internal state:
 *   - previewText: string - Example sentence shown in the preview area.
 *   - tempSelectedFont: string - Temporary selection that mirrors selectedFont when the modal opens and is used until Apply is clicked.
 * @component
 * @selector app-font-selection-modal
 * @example
 *   <app-font-selection-modal
 *     [isOpen]="isFontModalOpen"
 *     [title]="'Choose Font'"
 *     [selectedFont]="currentFont"
 *     [fonts]="availableFonts"
 *     (fontSelected)="onFontChosen($event)"
 *     (close)="onFontModalClose()"
 *   ></app-font-selection-modal>
 * @input boolean isOpen - Controls visibility of the modal; when true the modal opens and tempSelectedFont is initialized from selectedFont.
 * @input string title - Title displayed at the top of the modal (defaults to 'Heading').
 * @input string selectedFont - Currently applied font value; used to initialize tempSelectedFont when the modal opens.
 * @input FontOption[] fonts - Array of available font options to display for selection (each FontOption should include at least a value and label).
 * @output EventEmitter<void> close - Emitted when the modal should be closed (either via cancel, backdrop click, or after apply).
 * @output EventEmitter<string> fontSelected - Emitted with the newly applied font value when the user clicks Apply and the selection changed.
 */
@Component({
  selector: 'app-font-selection-modal',
  standalone: true,
  imports: [CommonModule],
  templateUrl: './font-selection-modal.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrls: ['./font-selection-modal.component.scss'],
})
export class FontSelectionModalComponent implements OnChanges {
  @Input() isOpen = false;
  @Input() title = 'Heading';
  @Input() selectedFont = '';
  @Input() fonts: FontOption[] = [];
  @Output() close = new EventEmitter<void>();
  @Output() fontSelected = new EventEmitter<string>();

  previewText = 'The five boxing wizards Jump quickly together';

  // Temporary selected font (not saved until Apply is clicked)
  tempSelectedFont = '';

  ngOnChanges(changes: SimpleChanges): void {
    // Initialize tempSelectedFont with the current selected font when modal opens
    if (changes['isOpen'] && this.isOpen) {
      this.tempSelectedFont = this.selectedFont;
    }
    if (changes['selectedFont'] && this.isOpen) {
      this.tempSelectedFont = this.selectedFont;
    }
  }

  onClose(): void {
    // Reset temp selection when closing without applying
    this.tempSelectedFont = this.selectedFont;
    this.close.emit();
  }

  onFontSelect(font: FontOption): void {
    // Update temporary selection (don't emit yet)
    this.tempSelectedFont = font.value;
  }

  onApply(): void {
    // Apply the font selection and close
    if (this.tempSelectedFont && this.tempSelectedFont !== this.selectedFont) {
      this.fontSelected.emit(this.tempSelectedFont);
    }
    this.close.emit();
  }

  onBackdropClick(event: MouseEvent): void {
    if ((event.target as HTMLElement).classList.contains('font-modal-overlay')) {
      this.onClose();
    }
  }

  trackByIndex(index: number): number {
    return index;
  }
  trackByValue(_: number, item: any): any {
    return item?.value ?? item;
  }
  trackById(_: number, item: any): any {
    return item?.id ?? item;
  }
  trackByString(_: number, s: any): any {
    return s;
  }
}
